import { API_BASE_URL } from '@/config/api';
import { api } from '@/services/api';
import {
  PUBLIC_AUTH_HEADERS,
  PUBLIC_HEADERS,
  publicGetParsed,
  publicPostParsed,
} from '@/lib/public-api';
import {
  ativarPlanoDashboardWireSchema,
  checkoutSuccessResponseSchema,
  checkoutTargetTokenResolveResponseSchema,
  contratacaoDashboardsResponseSchema,
  contratacaoPreviewRequestSchema,
  contratacaoPreviewResponseSchema,
  contratacaoResponseSchema,
  currentUserRequestSchema,
  currentUserResponseSchema,
  dashboardPlanoAtualSchema,
  hasCapabilityResponseSchema,
  mapAtivarPlanoRequestWithAttribution,
  publicPlanoSchema,
  publicPlanosListSchema,
  unwrapAtivarPlanoDashboardResponse,
  unwrapCheckoutTargetResolve,
  withAttribution,
  type ContratacaoRequest,
  type ContratacaoResponse,
  type ContratacaoPreviewRequest,
  type ContratacaoPreviewResponse,
  type ContratacaoDashboardOption,
  type DashboardAtivarPlanoRequest,
  type DashboardAtivarPlanoResponse,
  type DashboardPlanoAtual,
  type PlanoPublico,
  type CheckoutSuccessResponse,
} from '@/api/contratacao.actions.schema';
import { parseWith } from '@/lib/api-parse';

class ContratacaoService {
  /**
   * Lista todos os planos ativos disponíveis para contratação
   * GET /api/v2/public/planos
   */
  async listarPlanos(): Promise<PlanoPublico[]> {
    return publicGetParsed('/public/planos', publicPlanosListSchema);
  }

  /**
   * Lista dashboards vinculadas ao usuário logado.
   * Endpoint autenticado do workspace.
   */
  async listarDashboardsUsuario(): Promise<ContratacaoDashboardOption[]> {
    const wire = await api.getParsed(
      '/workspace/dashboard',
      contratacaoDashboardsResponseSchema,
    );
    return wire.data;
  }

  /**
   * Verifica se o usuário possui capability em uma dashboard.
   */
  async usuarioTemCapabilityNaDashboard(
    dashboardId: number,
    capability: string,
  ): Promise<boolean> {
    const wire = await api.getParsed(
      `/dashboard/${dashboardId}/me/has-capability?capability=${encodeURIComponent(
        capability,
      )}`,
      hasCapabilityResponseSchema,
    );
    return Boolean(wire.data?.hasCapability);
  }

  /**
   * Busca o plano atual de uma dashboard.
   */
  async buscarPlanoAtualDashboard(
    dashboardId: number,
  ): Promise<DashboardPlanoAtual> {
    return api.getParsed(
      `/dashboard/${dashboardId}/plano`,
      dashboardPlanoAtualSchema,
    );
  }

  /**
   * Resolve token assinado de checkout para dashboardId.
   */
  async resolverCheckoutTargetToken(
    token: string,
  ): Promise<{ valid: boolean; dashboardId?: number; reason?: string }> {
    const wire = await api.postParsed(
      '/auth/contratar/checkout-target-token/resolve',
      { token },
      checkoutTargetTokenResolveResponseSchema,
    );
    return unwrapCheckoutTargetResolve(wire);
  }

  /**
   * Busca um plano específico pelo slug
   * GET /api/v2/public/planos/:slug
   */
  async buscarPlanoPorSlug(slug: string): Promise<PlanoPublico> {
    const response = await fetch(
      `${API_BASE_URL}/public/planos/${encodeURIComponent(slug)}`,
      { headers: PUBLIC_HEADERS },
    );

    if (response.status === 404) {
      throw new Error(`Plano com slug "${slug}" não encontrado`);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        (error as { message?: string })?.message || 'Erro na requisição',
      );
    }

    const wire = await response.json();
    return parseWith(publicPlanoSchema, wire);
  }

  /**
   * Checkout autenticado: usa o cliente HTTP do painel (Bearer + renovação de sessão em 401).
   */
  async checkout(
    slug: string,
    dados: ContratacaoRequest,
  ): Promise<ContratacaoResponse> {
    return api.postParsed(
      `/auth/contratar/${slug}/checkout`,
      withAttribution(dados),
      contratacaoResponseSchema,
    );
  }

  async previewCheckout(
    slug: string,
    dados: ContratacaoPreviewRequest,
  ): Promise<ContratacaoPreviewResponse> {
    const body = contratacaoPreviewRequestSchema.parse(dados);
    return api.postParsed(
      `/auth/contratar/${slug}/preview`,
      body,
      contratacaoPreviewResponseSchema,
    );
  }

  /**
   * Checkout público (sem Bearer): usado no fluxo inline de autenticação/OTP em /contratar.
   * OTP guest: reenvio e validação ocorrem via este endpoint (`otpCode` opcional no body).
   */
  async checkoutPublic(
    slug: string,
    dados: ContratacaoRequest,
  ): Promise<ContratacaoResponse> {
    return publicPostParsed(
      `/auth/contratar/${slug}/checkout`,
      withAttribution(dados),
      contratacaoResponseSchema,
      { headers: PUBLIC_AUTH_HEADERS },
    );
  }

  /**
   * Retorna true quando o e-mail já possui conta cadastrada.
   */
  async emailTemConta(email: string): Promise<boolean> {
    const response = await fetch(`${API_BASE_URL}/auth/current`, {
      method: 'POST',
      credentials: 'include',
      headers: PUBLIC_AUTH_HEADERS,
      body: JSON.stringify(currentUserRequestSchema.parse({ email })),
    });

    if (response.status === 404 || response.status === 401) {
      return false;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        (error as { message?: string })?.message || 'Erro ao verificar conta',
      );
    }

    const wire = await response.json();
    parseWith(currentUserResponseSchema, wire);
    return true;
  }

  /**
   * Verifica status do checkout após pagamento no Stripe
   * GET /api/v2/auth/checkout-success?session_id={SESSION_ID}
   */
  async checkoutSuccess(sessionId: string): Promise<CheckoutSuccessResponse> {
    return publicGetParsed(
      `/auth/checkout-success?session_id=${encodeURIComponent(sessionId)}`,
      checkoutSuccessResponseSchema,
    );
  }

  /**
   * Ativa/reativa plano em dashboard existente.
   */
  async ativarPlanoDashboard(
    dashboardId: number,
    dados: DashboardAtivarPlanoRequest,
  ): Promise<DashboardAtivarPlanoResponse> {
    const wire = await api.postParsed(
      `/dashboard/${dashboardId}/plano/ativar`,
      mapAtivarPlanoRequestWithAttribution(dados),
      ativarPlanoDashboardWireSchema,
    );
    return unwrapAtivarPlanoDashboardResponse(wire);
  }
}

export const contratacaoService = new ContratacaoService();

export type {
  ContratacaoRequest,
  ContratacaoResponse,
  ContratacaoPreviewRequest,
  ContratacaoPreviewResponse,
  ContratacaoDashboardOption,
  DashboardAtivarPlanoRequest,
  DashboardAtivarPlanoResponse,
  DashboardPlanoAtual,
  PlanoPublico,
  CheckoutSuccessResponse,
};
